/* eslint-disable no-unused-vars */
import { createStandaloneToast } from '@chakra-ui/react';
import { conversationAction, homeAction, taskAction, translationAction } from 'actions';
import {
  Event as EventSourceType,
  EventSourcePolyfill,
  NativeEventSource,
} from 'event-source-polyfill';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getBearerToken } from './lib';
import store from './store';

const { toast } = createStandaloneToast();

export enum EventSourceTopic {
  CONNECTION_ID = 'connection_id',
  SYSTEM = 'system',
  ORGANIZATION = 'organization',
  USERS = 'users',
  CONVERSATION = 'CONVERSATION',
  CONVERSATION_STREAMING = 'CONVERSATION_STREAMING',
  SUMMARY = 'SUMMARY',
  SUMMARY_STREAMING = 'SUMMARY_STREAMING',
  FILE_STATUS = 'FILE_STATUS',
  TRANSLATE = 'TRANSLATE',
}

const HEARTBEAT_TIMEOUT_IN_MILLISECONDS = 6 * 60 * 60 * 1000;
export const EventSource = NativeEventSource || EventSourcePolyfill;

export const createConnection = (uri: string) => {
  return new EventSourcePolyfill(uri, {
    headers: {
      Authorization: getBearerToken() || '',
    },
    heartbeatTimeout: HEARTBEAT_TIMEOUT_IN_MILLISECONDS,
  });
};

interface useSSEProps {
  uri: string;
  onOpen: (this: EventSource, _ev: EventSourceType) => void;
  onMessage: (this: EventSource, _ev: EventSourceType) => void;
  onError: (this: EventSource, _ev: EventSourceType) => void;
  tokenAvailable: boolean;
}

const EventSourceTopicListenerList = [
  EventSourceTopic.CONNECTION_ID,
  EventSourceTopic.SYSTEM,
  EventSourceTopic.ORGANIZATION,
  EventSourceTopic.USERS,
  EventSourceTopic.CONVERSATION,
  EventSourceTopic.CONVERSATION_STREAMING,
  EventSourceTopic.SUMMARY,
  EventSourceTopic.SUMMARY_STREAMING,
  EventSourceTopic.FILE_STATUS,
  EventSourceTopic.TRANSLATE,
];

const useSSE = ({ uri, onOpen, onMessage, onError, tokenAvailable }: useSSEProps) => {
  const dispatch = useDispatch();
  const { current } = useSelector((state) => state.task);

  const [eventSource, setEventSource] = useState<EventSourcePolyfill>();
  const [noti, setNoti] = useState<any>(null);

  useEffect(() => {
    if (tokenAvailable) {
      const sse = createConnection(uri);
      setEventSource(sse);
      return () => {
        if (sse) sse.close();
      };
    }
  }, [uri, tokenAvailable]);

  const handleListenerMgs = (messageData: EventSourceType) => {
    const data = (messageData as MessageEvent).data;

    switch (messageData.type as EventSourceTopic) {
      case EventSourceTopic.SYSTEM:
        setNoti(JSON.parse(data));
        break;
      case EventSourceTopic.ORGANIZATION:
        setNoti(JSON.parse(data));
        break;
      case EventSourceTopic.USERS:
        setNoti(JSON.parse(data));
        break;

      case EventSourceTopic.CONVERSATION:
        dispatch(conversationAction.updateNewAnswer(JSON.parse(data)));
        dispatch(conversationAction.getConversations());
        break;

      case EventSourceTopic.CONVERSATION_STREAMING:
        dispatch(conversationAction.updateNewStreamingAnswerChunks(JSON.parse(data)));
        break;

      case EventSourceTopic.SUMMARY:
        dispatch(taskAction.updateNewAnswer(JSON.parse(data)));
        dispatch(taskAction.getTasks());
        dispatch(taskAction.getDetailTask(current));
        break;

      case EventSourceTopic.SUMMARY_STREAMING:
        dispatch(taskAction.updateNewStreamingAnswerChunksTask(JSON.parse(data)));
        break;

      case EventSourceTopic.FILE_STATUS:
        dispatch(taskAction.updateFileStatus(JSON.parse(data)));
        break;

      case EventSourceTopic.TRANSLATE:
        dispatch(translationAction.updateTranslatedFileStatus(JSON.parse(data)));
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    if (eventSource) {
      eventSource.onopen = onOpen;
      eventSource.onmessage = onMessage;
      eventSource.onerror = onError;

      for (const eventTopic of EventSourceTopicListenerList) {
        eventSource.addEventListener(eventTopic, handleListenerMgs);
      }
    }
    return () => {
      if (eventSource) {
        for (const eventTopic of EventSourceTopicListenerList) {
          eventSource.removeEventListener(eventTopic, handleListenerMgs);
        }
      }
    };
  }, [eventSource, onOpen, onMessage, onError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (noti) {
      toast({
        position: 'bottom-right',
        isClosable: true,
        title: noti?.message?.en,
        status:
          noti?.severity?.toLowerCase() === 'info' ? 'success' : noti?.severity?.toLowerCase(),
      });
      store.dispatch(homeAction.fetchListNotification());
    }

    setNoti(null);
  }, [noti]);
  return eventSource;
};

export default useSSE;
